import { RefObject, useEffect } from "react";

export const stickyBottomBarStuckClass = "lv2-sticky--stuck";
const leavingClass = "lv2-sticky--leaving";
/** Matches the slide-down animation in LanderV2.css. */
const leaveDurationMs = 200;
/** How far the chat widget lifts to clear a pinned bar (read by ThreeCXWidget). */
export const chatOffsetProperty = "--lv2-chat-offset";

interface StickyBottomBarOptions {
  enabled: boolean;
  /** The bar (holding the button) inside the container that gets pinned. */
  barSelector: string;
  /**
   * The element whose width the pinned button takes: the container's nearest matching ancestor, else a matching
   * descendant, else the first match on the page.
   */
  widthSelector: string;
  /** Re-runs the effect when the container's content changes (e.g. a new step). */
  resetKey?: string;
}

/**
 * Pins a button bar to the bottom of the screen while the bar's own place is below the fold, sitting just above the
 * cookie banner when that is showing, and lifts the chat widget above it. The bar slides up as it is pinned and back
 * down before it returns to its place. While pinned, the container keeps the bar's space so the page doesn't jump.
 * Styled by `.lv2-sticky--stuck` in LanderV2.css.
 */
export function useStickyBottomBar(
  containerRef: RefObject<HTMLElement | null>,
  { enabled, barSelector, widthSelector, resetKey }: StickyBottomBarOptions
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return;

    let frame = 0;
    let barHeight = 0;
    let leaveTimer: number | undefined;
    let ownsChatOffset = false;

    const setChatOffset = (offset: number) => {
      ownsChatOffset = offset > 0;
      document.documentElement.style.setProperty(
        chatOffsetProperty,
        `${offset}px`
      );
    };

    const unpin = () => {
      leaveTimer = undefined;
      container.classList.remove(stickyBottomBarStuckClass, leavingClass);
      container.style.paddingBottom = "";
      schedule();
    };

    const update = () => {
      frame = 0;
      const bar = container.querySelector<HTMLElement>(barSelector);
      // The width reference stays in the flow while the bar is pinned.
      const widthElement =
        container.closest<HTMLElement>(widthSelector) ??
        container.querySelector<HTMLElement>(widthSelector) ??
        document.querySelector<HTMLElement>(widthSelector);
      if (!bar || !widthElement) return;

      const banner = document.querySelector<HTMLElement>(".cookie-consent");
      const bottomOffset = banner ? banner.getBoundingClientRect().height : 0;
      const isStuck = container.classList.contains(stickyBottomBarStuckClass);
      const isLeaving = container.classList.contains(leavingClass);
      if (!isStuck) barHeight = bar.getBoundingClientRect().height;

      // The container ends where the bar would sit (its space is kept while it is pinned).
      const shouldStick =
        container.getBoundingClientRect().bottom >
        window.innerHeight - bottomOffset;
      container.style.setProperty("--lv2-sticky-bottom", `${bottomOffset}px`);
      container.style.setProperty(
        "--lv2-sticky-button-width",
        `${widthElement.getBoundingClientRect().width}px`
      );

      if (shouldStick) {
        if (isLeaving) {
          window.clearTimeout(leaveTimer);
          leaveTimer = undefined;
          container.classList.remove(leavingClass);
        }
        if (!isStuck) {
          container.classList.add(stickyBottomBarStuckClass);
          container.style.paddingBottom = `${barHeight}px`;
          // Measure the pinned bar on the next frame to lift the chat clear of it.
          schedule();
          return;
        }
        setChatOffset(bar.getBoundingClientRect().height + bottomOffset);
      } else if (isStuck && !isLeaving) {
        container.classList.add(leavingClass);
        setChatOffset(0);
        leaveTimer = window.setTimeout(unpin, leaveDurationMs);
      }
    };

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Content height changes (e.g. a panel opening) and the cookie banner coming or going.
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(container);
    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    schedule();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(leaveTimer);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      container.classList.remove(stickyBottomBarStuckClass, leavingClass);
      container.style.paddingBottom = "";
      if (ownsChatOffset) setChatOffset(0);
    };
  }, [containerRef, enabled, barSelector, widthSelector, resetKey]);
}
